/**
 * 8 - Liberate a P.O.W. Camp (03 §2): blow the concrete controls pyramid to expose the
 * lever, kick it to open the tiger pits, bring the 3-6 prisoners out. Any shot or blast
 * before the ranger is inside the goal zone and the prisoners are trucked away.
 */

import type { Point } from '../types';
import {
  checker, clutter, countKind, guardAtDoor, guardsAbout, insideRect, required, ruleOf, structure,
} from './helpers';
import { flagTargets } from './helpers3b';
import type { ArchetypeSpec } from './types';

/** Captive ids sit apart from prop ids (< 700), pods (900+) and soldiers (1000+). */
const FIRST_CAPTIVE_ID = 700;

export const powCamp: ArchetypeSpec = {
  id: 'pow-camp',
  name: 'LIBERATE A P.O.W. CAMP',
  nativeBiome: 'temperate',
  baseThreat: 5,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [48, 58], d: [30, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const placed: Point[] = [];
    const pits = 3 + g.rng.int(4);
    // The pits are dug together in one corner of the camp.
    const west = g.rng.chance(0.5);
    const mid = (g.inner.x0 + g.inner.x1) / 2;
    const yard = { ...g.inner, x0: west ? g.inner.x0 : mid, x1: west ? mid : g.inner.x1 };
    for (let i = 0; i < pits; i++) {
      const pit = i < 3 ? required(g, 'tigerPit', { within: yard, minDist: 5, from: placed, margin: 0.9 })
        : structure(g, 'tigerPit', { within: yard, minDist: 5, from: placed, margin: 0.9 });
      if (pit) g.map.captives.push({ id: FIRST_CAPTIVE_ID + g.map.captives.length, kind: 'pow', x: pit.x, z: pit.z });
    }
    // The pyramid is the target that gets a sentry post; both it and the lever are marked (see `finish`).
    const pyramid = required(g, 'controlsPyramid', { target: true, minDist: 9, from: placed, margin: 2 });
    // The lever is inside the pyramid until that is blown away.
    const lever = g.map.add('lever', pyramid.x, pyramid.z);
    const house = structure(g, 'guardHouse', { minDist: 7, from: placed });
    structure(g, g.kit.shelter, { minDist: 7, from: placed });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);

    if (house) guardAtDoor(g, house);
    guardAtDoor(g, pyramid);
    guardsAbout(g, 1 + g.rng.int(2));
    const zone = { ...g.rect };
    return {
      objective: {
        kind: 'freeAndEscort', label: 'FREE PRISONERS', targetPropIds: [pyramid.id, lever.id], minFraction: 1,
        rules: [
          { type: 'quietUntilZone', zone, breach: 'fail' },
          { type: 'alertConsequence', level: 3, delaySeconds: 30, unlessInsideZone: zone, effect: 'prisonersMoved' },
          { type: 'recall', mode: 'normal' },
        ],
      },
      // ROUND5 §2.3 (the marker audit): the lever was named as a target and never flagged - it showed on the
      // strip map only by accident, because it shares the pyramid's x and z. Say it. Flagged here rather than
      // at build time because a flag then posts a second sentry (posts.ts) on the spot the pyramid's already has.
      finish(def) {
        flagTargets(def, [pyramid.id, lever.id]);
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('pow-camp');
    const pits = def.props.filter((p) => p.kind === 'tigerPit');
    check(pits.length >= 3 && pits.length <= 6, `needs 3-6 tiger pits, has ${pits.length}`);
    check(
      countKind(def, 'controlsPyramid') === 1 && countKind(def, 'lever') === 1,
      'needs one controls pyramid and one lever',
    );
    const [first, second] = def.objective.targetPropIds;
    const marks = [first, second].map((id) => def.props.find((p) => p.id === id));
    const kinds = marks.map((p) => p?.kind);
    check(
      def.objective.kind === 'freeAndEscort' && kinds[0] === 'controlsPyramid' && kinds[1] === 'lever',
      'targets must be [pyramid, lever]',
    );
    // ROUND5: both of them are on the strip map on purpose - the pyramid to blow, and the lever under it
    // to kick once it is gone. The lever's mark used to be an accident of sharing the pyramid's x, z.
    check(marks.every((p) => p?.target === true), 'both the pyramid and the lever must be flagged for the strip map');
    check(
      def.captives.length === pits.length && def.captives.every((c) => c.kind === 'pow'),
      'one P.O.W. per tiger pit',
    );
    for (const c of def.captives) {
      check(
        pits.some((p) => Math.abs(p.x - c.x) < 1.3 && Math.abs(p.z - c.z) < 1.3),
        `prisoner ${c.id} is not in a pit`,
      );
      check(insideRect(def.goalZone, c.x, c.z), `prisoner ${c.id} is outside the goal zone`);
    }
    check(ruleOf(def, 'quietUntilZone')?.breach === 'fail', 'a shot before the goal zone must fail the mission');
    return out;
  },
};
